import React, {
	useCallback,
	useEffect,
} from 'react';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import {
	faGasPump,
	faList,
	faPlus,
} from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
	Flex,
	Space,
} from 'antd';
import { faSort } from '@fortawesome/pro-solid-svg-icons';
import Select from 'antd/lib/select';
import { TimeZoneFormat } from '@shared/utils/constants';
import FormPopover from '@client/components/FormPopover';
import Button from '@client/components/Button';
import useFetchedState from '@client/utils/hooks/useFetchedState';
import {
	getUserData,
	updateFixture,
	updateUser,
} from '@client/lib/api';
import showErrorNotification from '@client/utils/showErrorNotification';
import { useVoyage } from '@client/screens/fleet/VoyageDetailsScreen/components/VoyageProvider/VoyageProvider';
import { useMiscHandlers } from '@client/screens/estimates/details/context/hooks/useMiscHandlers';
import { useItinerary } from '../ItineraryProvider';
import getFormFields, { NewPortCallFormValues } from '../utils/newPortCallForm';
import {
	getPortTimezone,
	normalizePortDateTime,
} from './PortAwareDatePicker';

const { UTC, LocalTime } = TimeZoneFormat;

type Props = {
	saveButton: React.ReactElement | null;
};

const ItineraryControls = ({ saveButton }: Props) => {
	const {
		itinerary,
		setViewMode,
		addItineraryEntry,
		viewMode,
		setSortDirection,
		sortDirection,
		timeZoneFormat,
		setTimeZoneFormat,
	} = useItinerary();

	const {
		portOptions,
	} = useMiscHandlers();

	const {
		fixtureDetails,
		refreshFixtureDetails,
	} = useVoyage();

	const [userData, refreshUserData] = useFetchedState(getUserData);

	const formFields = getFormFields(portOptions, itinerary, timeZoneFormat);

	useEffect(() => {
		if (userData?.itinerarySortDirection != null) {
			setSortDirection(userData?.itinerarySortDirection as 'asc' | 'desc');
		}
	}, [userData?.itinerarySortDirection, setSortDirection, sortDirection]);

	useEffect(() => {
		setTimeZoneFormat(fixtureDetails?.selectedTimeFormat ?? UTC);
	}, [fixtureDetails?.selectedTimeFormat, setTimeZoneFormat]);

	const updateSortDirection = async () => {
		const updatedSortDirection = sortDirection === 'asc' ? 'desc' : 'asc';
		await updateUser({
			userId: userData?.id,
			attributes:
				{ itinerarySortDirection: updatedSortDirection },
		});
		await refreshUserData();
		setSortDirection(updatedSortDirection);
	};

	const handleUpdateTimeFormat = useCallback(async (updatedTimeFormat: TimeZoneFormat) => {
		if (fixtureDetails == null) {
			return;
		}

		setTimeZoneFormat(updatedTimeFormat);

		try {
			await updateFixture(fixtureDetails.id, { selectedTimeFormat: updatedTimeFormat });
			await refreshFixtureDetails();
		} catch (_e) {
			showErrorNotification('Time format could not be set');
			setTimeZoneFormat(fixtureDetails?.selectedTimeFormat ?? UTC);
		}
	}, [fixtureDetails, refreshFixtureDetails, setTimeZoneFormat]);

	const handleAddItineraryEntry = useCallback((values: NewPortCallFormValues) => {
		const selectedTimeZoneFormat = values.timeZoneFormat ?? timeZoneFormat;
		const timezone = getPortTimezone(values.portId);

		return addItineraryEntry({
			portId: values.portId,
			robs: values.robs,
			estimatedDepartureDate: normalizePortDateTime(
				values.estimatedDepartureDate,
				selectedTimeZoneFormat,
				timezone,
			) ?? undefined,
			actualDepartureDate: normalizePortDateTime(
				values.actualDepartureDate,
				selectedTimeZoneFormat,
				timezone,
			) ?? undefined,
		});
	}, [addItineraryEntry, timeZoneFormat]);

	return (
		<Flex justify="space-between" align="center">
			<Space>
				<FormPopover<NewPortCallFormValues>
					title="Add planned port call"
					popoverWidth={550}
					// @ts-ignore
					fields={formFields}
					initialValues={{ timeZoneFormat }}
					buttonText={(
						<Space>
							<FontAwesomeIcon icon={faPlus as IconProp} />
							Add Planned Port Call
						</Space>
					)}
					buttonProps={{ disabledTooltip: 'Cannot add entries after completion of voyage' }}
					onSubmit={handleAddItineraryEntry}
				/>
				<Button
					disabled={viewMode === 'default'}
					onClick={() => setViewMode('default')}
					type="link"
					icon={(
						<FontAwesomeIcon icon={faList as IconProp} />
					)}
				>
					Default view
				</Button>
				<Button
					disabled={viewMode === 'bunker'}
					onClick={() => setViewMode('bunker')}
					type="link"
					icon={(
						<FontAwesomeIcon icon={faGasPump as IconProp} />
					)}
				>
					Bunker view
				</Button>
				<Button
					onClick={updateSortDirection}
					type="link"
					icon={(<FontAwesomeIcon icon={faSort as IconProp} />)}
				>
					{sortDirection === 'desc' ? 'Sort oldest to newest' : 'Sort newest to oldest'}
				</Button>
			</Space>
			<Flex gap={10} align='center'>
				{saveButton}
				<div style={{ marginLeft: 'auto', width: 150 }}>
					<Select
						options={[
							{ label: 'UTC', value: UTC },
							{ label: 'Local Time', value: LocalTime },
						]}
						value={timeZoneFormat}
						onChange={handleUpdateTimeFormat}
					/>
				</div>
			</Flex>
		</Flex>
	);
};

export default ItineraryControls;
